import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { environment } from '#environments';
import {
    CostCodeSummary,
    CreateCostCode,
    PaginatedResult,
    UpdateCostCode,
    BulkUploadCostCodesResponse,
    GetCostCodeBulkUploadJobStatusResponse,
    RemediationCostCode,
    LinkCostCode,
} from '#models';

export interface CostCodeFilters {
    search?: string;
    page?: number;
    pageSize?: number;
}

@Injectable({ providedIn: 'root' })
export class CostCodeService {
    private _http = inject(HttpClient);
    private _base = `${environment.apiBaseUrl}/v1/cost-codes`;
    private _remediationsBase = `${environment.apiBaseUrl}/v1/remediations`;

    /**
     * Get paginated list of cost codes with optional filters
     */
    getCostCodes(filters: CostCodeFilters = {}): Observable<PaginatedResult<CostCodeSummary>> {
        const params: Record<string, string> = {};
        if (filters.search) params['search'] = filters.search;
        if (filters.page != null) params['page'] = String(filters.page);
        if (filters.pageSize != null) params['pageSize'] = String(filters.pageSize);
        return this._http.get<PaginatedResult<CostCodeSummary>>(this._base, { params });
    }

    /**
     * Create a new cost code
     */
    createCostCode(dto: CreateCostCode): Observable<CostCodeSummary> {
        return this._http.post<CostCodeSummary>(this._base, dto);
    }

    /**
     * Update an existing cost code
     */
    updateCostCode(id: string, dto: UpdateCostCode): Observable<CostCodeSummary> {
        const requestBody = {
            code: dto.code,
            description: dto.description,
            chevronCompanyCode: dto.chevronCompanyCode,
        };

        return this._http.put<CostCodeSummary>(`${this._base}/${id}`, requestBody);
    }

    /**
     * Delete a cost code
     */
    deleteCostCode(id: string): Observable<void> {
        return this._http.delete<void>(`${this._base}/${id}`);
    }

    /**
     * Uploads a CSV file for async Cost Code bulk upload/association processing.
     * Returns 202 Accepted with a job ID.
     */
    uploadCsv(file: File): Observable<BulkUploadCostCodesResponse> {
        const formData = new FormData();
        formData.append('csvFile', file, file.name);
        return this._http.post<BulkUploadCostCodesResponse>(`${this._base}/bulk-upload`, formData);
    }

    /**
     * Get bulk upload job status
     */
    getBulkUploadStatus(jobId: string): Observable<GetCostCodeBulkUploadJobStatusResponse> {
        return this._http.get<GetCostCodeBulkUploadJobStatusResponse>(`${this._base}/bulk-upload-jobs/${jobId}`);
    }

    /**
     * Downloads the error report JSON for a bulk upload job by streaming it through the API,
     * rather than requesting the (unsigned, non-routable) blob storage URL returned in the job
     * status payload.
     */
    downloadErrorReport(jobId: string): Observable<Blob> {
        return this._http.get(`${this._base}/bulk-upload-jobs/${jobId}/error-report`, { responseType: 'blob' });
    }

    /**
     * Links a cost code to a remediation via the existing Remediation ↔ CostCode association endpoint.
     */
    linkToRemediation(remediationId: string, dto: LinkCostCode): Observable<RemediationCostCode> {
        return this._http.post<RemediationCostCode>(`${this._remediationsBase}/${remediationId}/cost-codes`, dto);
    }

    /**
     * Unlinks a cost code from a remediation.
     */
    unlinkFromRemediation(remediationId: string, remediationCostCodeId: string): Observable<void> {
        return this._http.delete<void>(`${this._remediationsBase}/${remediationId}/cost-codes/${remediationCostCodeId}`);
    }

    /**
     * Returns all cost codes currently linked to the given remediation (by Remediation ID / UPI).
     */
    getByRemediation(remediationId: string): Observable<RemediationCostCode[]> {
        return this._http.get<RemediationCostCode[]>(`${this._remediationsBase}/${remediationId}/cost-codes`);
    }

    /**
     * Download cost codes as CSV
     * (Stub: will be implemented in Phase 3+)
     */
    downloadCsv(_filters?: CostCodeFilters): Observable<Blob> {
        // TODO: Implement in Phase 3+
        return of(new Blob([''], { type: 'text/csv;charset=utf-8' }));
    }
}
